import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Card,
    CardContent,
    Chip,
    IconButton,
    CircularProgress,
    Alert,
    useTheme,
    useMediaQuery,
    Divider,
    Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';

const PendingOrdersModal = ({
    open,
    onClose,
    pendingOrders,
    onCancelOrder,
    cancellingOrders = [],
    title = "Pending Orders Conflict",
    message = "Please cancel the following pending orders before proceeding with square-off:",
    onProceed
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [error, setError] = useState(null);

    const getOrderTypeColor = (orderType) => {
        switch (orderType) {
            case 'MARKET': return theme.palette.info.main;
            case 'LIMIT': return theme.palette.primary.main;
            case 'SL': return theme.palette.warning.main;
            case 'SLM': return theme.palette.error.main;
            default: return theme.palette.text.secondary;
        }
    };

    const getStatusColor = (status) => {
        if (status.includes('PENDING')) return theme.palette.warning.main;
        if (status.includes('CONFIRMED')) return theme.palette.success.main;
        if (status.includes('REJECTED')) return theme.palette.error.main;
        return theme.palette.info.main;
    };

    const handleCancelOrder = async (order) => {
        try {
            setError(null);
            await onCancelOrder(order);
        } catch (err) {
            setError(err.message || 'Failed to cancel order');
        }
    };

    const handleClose = () => {
        setError(null);
        onClose();
    };

    const handleProceed = () => {
        if (pendingOrders.length === 0 && onProceed) {
            onProceed();
        } else {
            handleClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3,
                    maxHeight: isMobile ? '100vh' : '80vh'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon sx={{ color: theme.palette.warning.main }} />
                    <Typography variant="h6" fontWeight="bold">
                        {title}
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                <Box sx={{ p: 2 }}>
                    <Alert
                        severity={pendingOrders.length === 0 ? "success" : "warning"}
                        variant="filled"
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            backgroundColor: pendingOrders.length === 0
                                ? alpha(theme.palette.success.main, 0.9)
                                : alpha(theme.palette.warning.main, 0.9),
                            color: 'white',
                            '& .MuiAlert-icon': { color: 'white' }
                        }}
                    >
                        <Typography variant="body2" fontWeight="medium">
                            {pendingOrders.length === 0
                                ? "No pending orders found. You can proceed with square-off."
                                : message}
                        </Typography>
                    </Alert>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{
                        maxHeight: isMobile ? '50vh' : '40vh',
                        overflow: 'auto',
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-track': { backgroundColor: alpha(theme.palette.divider, 0.1) },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: alpha(theme.palette.warning.main, 0.3),
                            borderRadius: 3
                        }
                    }}>
                        {pendingOrders.length === 0 ? (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 4,
                                textAlign: 'center'
                            }}>
                                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                    No Pending Orders
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    There are no pending orders to cancel. You can proceed with square-off.
                                </Typography>
                            </Box>
                        ) : (
                            pendingOrders.map((order, index) => (
                                <Card
                                    key={order.oms_order_id}
                                    elevation={0}
                                    sx={{
                                        mb: 1,
                                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                        borderRadius: 2,
                                        '&:last-child': { mb: 0 }
                                    }}
                                >
                                    <CardContent sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip
                                                    label={order.order_side}
                                                    size="small"
                                                    variant="filled"
                                                    sx={{
                                                        backgroundColor: order.order_side === 'BUY' ? theme.palette.success.main : theme.palette.error.main,
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                                <Chip
                                                    label={order.order_type}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        borderColor: getOrderTypeColor(order.order_type),
                                                        color: getOrderTypeColor(order.order_type)
                                                    }}
                                                />
                                                <Chip
                                                    label={order.product}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        borderColor: alpha(theme.palette.text.secondary, 0.3)
                                                    }}
                                                />
                                            </Box>

                                            <Tooltip title="Cancel Order" arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCancelOrder(order)}
                                                    disabled={cancellingOrders.includes(order.oms_order_id)}
                                                    sx={{
                                                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                                                        color: 'error.main',
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.error.main, 0.2)
                                                        }
                                                    }}
                                                >
                                                    {cancellingOrders.includes(order.oms_order_id) ? (
                                                        <CircularProgress size={16} />
                                                    ) : (
                                                        <CancelIcon sx={{ fontSize: '1rem' }} />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(120px, 1fr))',
                                            gap: 1.5,
                                            alignItems: 'center'
                                        }}>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Symbol:</strong> {order.script_name || order.trading_symbol}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Qty:</strong> {order.quantity}
                                            </Typography>
                                            {order.price > 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Price:</strong> ₹{order.price.toFixed(2)}
                                                </Typography>
                                            )}
                                            <Chip
                                                label={order.order_status}
                                                size="small"
                                                variant="filled"
                                                sx={{
                                                    backgroundColor: alpha(getStatusColor(order.order_status), 0.15),
                                                    color: getStatusColor(order.order_status),
                                                    fontWeight: 'medium'
                                                }}
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{ minWidth: 100 }}
                >
                    Close
                </Button>
                <Button
                    onClick={handleProceed}
                    variant="contained"
                    disabled={pendingOrders.length > 0}
                    sx={{
                        minWidth: 100,
                        backgroundColor: pendingOrders.length === 0 ? theme.palette.success.main : theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: pendingOrders.length === 0 ? theme.palette.success.dark : theme.palette.primary.dark
                        }
                    }}
                >
                    {pendingOrders.length > 0 ? 'Cancel All Orders First' : 'Proceed with Square-off'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PendingOrdersModal;
